import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Music, Calendar, MapPin, ChevronRight, Sparkles, Tent } from "lucide-react";

/**
 * Homepage strip — "What festivals are you near?" tile.
 *
 * iter122 (v2) — Rebuilt with much stronger visual hierarchy + per-card
 * imagery. Each card has a tinted festival photo background, big D-N
 * countdown overlay, bold festival name, and audience pill. Section
 * border is now neon-orange so it pops out of the page flow.
 *
 * Auto-hides if there are zero upcoming festivals so the homepage never
 * has an empty slot.
 */

const AUDIENCE_TINT = {
  family: "#16a34a",
  adults: "#9333ea",
  rowdy: "#dc2626",
  boutique: "#0891b2",
  mixed: "#0e0e0e",
};

// Per-audience evocative background imagery (royalty-free Unsplash, small +
// optimised). Used only when the festival itself has no `image` field. The
// hero_color overlay sits on top so even if the photo fails to load, the
// card still pops with the festival's brand colour.
const AUDIENCE_BG = {
  family: "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&q=70&auto=format&fit=crop",
  adults: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&q=70&auto=format&fit=crop",
  rowdy: "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?w=600&q=70&auto=format&fit=crop",
  boutique: "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=600&q=70&auto=format&fit=crop",
  mixed: "https://images.unsplash.com/photo-1429962714451-bb934ecdc4ec?w=600&q=70&auto=format&fit=crop",
};

function FestivalTileCard({ f }) {
  const tint = AUDIENCE_TINT[f.audience] || AUDIENCE_TINT.mixed;
  const bg = f.image || AUDIENCE_BG[f.audience] || AUDIENCE_BG.mixed;
  const dLabel = f.days_until == null ? "" :
                 f.days_until === 0 ? "TODAY" :
                 f.days_until === 1 ? "TOMORROW" :
                 `D-${f.days_until}`;
  return (
    <Link
      to={`/festivals/${f.slug}`}
      className="group block flex-shrink-0 w-[280px] sm:w-[320px] relative overflow-hidden border-2 border-[#0e0e0e] bg-[#0e0e0e] hover:shadow-[0_4px_0_0_#ff4500] hover:-translate-y-1 transition-all duration-200"
      data-testid={`homepage-festival-${f.slug}`}
    >
      {/* Tinted photo background — covers the whole card. */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${bg})` }}
        aria-hidden="true"
      />
      {/* Festival-color tint overlay so the brand pops through. */}
      <div
        className="absolute inset-0 mix-blend-multiply opacity-70"
        style={{ background: f.hero_color || "#0e0e0e" }}
        aria-hidden="true"
      />
      {/* Black bottom-shade so the text always reads. */}
      <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent" aria-hidden="true" />

      <div className="relative aspect-[4/5] p-4 flex flex-col text-white">
        {/* Top row — countdown + audience pill */}
        <div className="flex items-start justify-between mb-auto">
          {dLabel && (
            <div className="inline-flex items-center gap-1 bg-[#ff4500] text-white px-2 py-1 text-[10px] mono uppercase tracking-[0.18em] font-bold shadow-[0_2px_0_0_#0e0e0e]">
              <Sparkles className="w-3 h-3" /> {dLabel}
            </div>
          )}
          <div className="text-[10px] mono uppercase tracking-wider text-white/90 px-2 py-1 backdrop-blur-sm" style={{ background: tint }}>
            {f.audience}
          </div>
        </div>

        {/* Big D-N number "watermark" — visual hook. */}
        {f.days_until != null && f.days_until > 1 && f.days_until <= 365 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 font-display mono text-[110px] leading-none text-white/15 select-none pointer-events-none">
            {f.days_until}
          </div>
        )}

        {/* Bottom content */}
        <div>
          <h3 className="font-display text-2xl leading-[1.05] tracking-tight mb-2 line-clamp-2 group-hover:text-[#ff4500] transition-colors">
            {f.name}
          </h3>
          <div className="text-[11px] mono uppercase tracking-wider text-white/90 flex items-center gap-1.5 mb-1">
            <Calendar className="w-3 h-3 flex-shrink-0" />
            {new Date(f.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} →{" "}
            {new Date(f.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
          </div>
          <div className="text-[11px] mono uppercase tracking-wider text-white/80 flex items-center gap-1.5 truncate mb-3">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{f.location_label}</span>
          </div>
          <div className="inline-flex items-center gap-1.5 text-xs font-display bg-white text-[#0e0e0e] px-3 py-1.5 group-hover:bg-[#ff4500] group-hover:text-white transition-colors">
            <Tent className="w-3.5 h-3.5" /> Find a pitch <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function FestivalsHomepageStrip() {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    api.get("/festivals/upcoming?limit=4").then(({ data }) => {
      if (alive) setFestivals(data?.festivals || []);
    }).catch(() => { /* swallow */ })
    .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (festivals.length === 0) return null;

  return (
    <section
      className="relative bg-gradient-to-b from-[#0e0e0e] via-[#1a0a05] to-[#0e0e0e] text-white border-y-4 border-[#ff4500]"
      data-testid="homepage-festivals-strip"
    >
      {/* Diagonal "stage lights" gradient — gives the section depth. */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 15% 30%, rgba(255,69,0,0.45) 0%, transparent 35%), radial-gradient(circle at 85% 70%, rgba(147,51,234,0.35) 0%, transparent 35%)",
        }}
        aria-hidden="true"
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
          <div>
            <div className="eyebrow text-[#ff4500] mb-2 inline-flex items-center gap-2 text-sm">
              <Music className="w-3.5 h-3.5" strokeWidth={2.4} /> Festival camping · 2026
            </div>
            <h2 className="font-display text-3xl sm:text-5xl tracking-tight leading-[0.95]">
              Heading to a festival?
            </h2>
            <p className="text-base text-white/80 max-w-xl mt-2 leading-relaxed">
              We've mapped vetted campsites near every major UK festival — tagged by who they suit.
              Pick yours, skip the 11pm Google Maps panic.
            </p>
          </div>
          <Link
            to="/festivals"
            className="inline-flex items-center gap-1.5 px-5 py-3 bg-[#ff4500] text-white hover:bg-white hover:text-[#0e0e0e] transition-colors text-xs mono uppercase tracking-[0.18em] font-bold shadow-[0_3px_0_0_#fff]"
            data-testid="homepage-festivals-cta"
          >
            All festivals <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Horizontal scroll on mobile, 4-col grid on lg. */}
        <div className="flex gap-4 overflow-x-auto pb-3 -mx-6 lg:-mx-10 px-6 lg:px-10 lg:grid lg:grid-cols-4 lg:overflow-visible snap-x snap-mandatory">
          {festivals.map((f) => (
            <div key={f.slug} className="snap-start">
              <FestivalTileCard f={f} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
